const isDevelopment = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
    module: string;
    level?: LogLevel;
    enabled?: boolean;
}

interface LogData {
    [key: string]: any;
}

class Logger {
    private module: string;
    private enabled: boolean;
    private level: LogLevel;

    constructor({ module, level = 'info', enabled = isDevelopment }: LoggerOptions) {
        this.module = module;
        this.level = level;
        this.enabled = enabled;
    }

    private shouldLog(level: LogLevel): boolean {
        if (!this.enabled) return false;
        
        const levels: Record<LogLevel, number> = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3
        };

        return levels[level] >= levels[this.level];
    }

    private formatMessage(message: string, data?: LogData): string {
        const timestamp = new Date().toISOString();
        const dataString = data ? ` ${JSON.stringify(data)}` : '';
        return `[${timestamp}] [${this.module}] ${message}${dataString}`;
    }

    debug(message: string, data?: LogData): void {
        if (this.shouldLog('debug')) {
            console.debug(this.formatMessage(message, data));
        }
    }

    info(message: string, data?: LogData): void {
        if (this.shouldLog('info')) {
            console.info(this.formatMessage(message, data));
        }
    }

    warn(message: string, data?: LogData | unknown): void {
        if (this.shouldLog('warn')) {
            let logData: LogData | undefined;
            
            // Check if data is a valid LogData object or needs conversion
            if (data !== undefined) {
                if (data === null || typeof data !== 'object' || Array.isArray(data)) {
                    // Handle non-object data by converting to a LogData object
                    logData = { value: data, type: typeof data };
                } else {
                    try {
                        // Try to use as LogData directly, but safely typecast
                        logData = data as LogData;
                    } catch (e) {
                        // Fallback if typecasting fails
                        logData = { value: String(data), type: typeof data };
                    }
                }
            }
            
            console.warn(this.formatMessage(message, logData));
        }
    }

    error(message: string, error?: Error | unknown, data?: LogData): void {
        if (this.shouldLog('error')) {
            let errorData: LogData = {};
            
            // Add error information to the log data
            if (error !== undefined) {
                if (error instanceof Error) {
                    // Handle standard Error objects
                    errorData = {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    };
                } else if (error !== null && typeof error === 'object') {
                    // Handle object-like errors that aren't Error instances
                    try {
                        // Try to JSON.stringify the error first
                        const jsonStr = JSON.stringify(error, (key, value) => {
                            // Handle circular references
                            if (typeof value === 'object' && value !== null) {
                                if (value instanceof Error) {
                                    return {
                                        name: value.name,
                                        message: value.message,
                                        stack: value.stack,
                                        ...value
                                    };
                                }
                            }
                            return value;
                        });
                        
                        // If we can stringify it, parse it back to get a clean object
                        if (jsonStr && jsonStr !== '{}') {
                            errorData = JSON.parse(jsonStr);
                        } else {
                            // Fallback to manual extraction
                            const errorObj = error as Record<string, any>;
                            
                            // Extract all enumerable properties
                            errorData = {};
                            for (const key in errorObj) {
                                try {
                                    const value = errorObj[key];
                                    if (typeof value !== 'function') {
                                        errorData[key] = value;
                                    }
                                } catch {
                                    // Skip properties that throw on access
                                }
                            }
                            
                            // Also get non-enumerable properties
                            const propNames = Object.getOwnPropertyNames(errorObj);
                            for (const key of propNames) {
                                if (!(key in errorData)) {
                                    try {
                                        const value = errorObj[key];
                                        if (typeof value !== 'function' && key !== 'constructor') {
                                            errorData[key] = value;
                                        }
                                    } catch {
                                        // Skip properties that throw on access
                                    }
                                }
                            }
                            
                            // Add type info
                            errorData._type = errorObj.constructor?.name || 'Object';
                            errorData._toString = String(error);
                        }
                    } catch (e) {
                        // Final fallback
                        errorData = { 
                            _error: String(error),
                            _extractionError: String(e)
                        };
                    }
                } else {
                    // Handle primitive error types
                    errorData = { 
                        error_value: error,
                        error_type: typeof error
                    };
                }
            }
            
            // Merge with any additional data provided
            if (data) {
                errorData = { ...errorData, ...data };
            }
            
            console.error(this.formatMessage(message, errorData));
        }
    }

    // Special method for component renders - only logs in debug mode and throttled
    render(data?: any) {
        if (this.shouldLog('debug')) {
            // Using a WeakMap to store last render time per component
            if (!Logger.renderTimes.has(this)) {
                Logger.renderTimes.set(this, 0);
            }

            const now = Date.now();
            const lastRender = Logger.renderTimes.get(this) || 0;

            // Only log if more than 1 second has passed since last render
            if (now - lastRender > 1000) {
                console.debug(this.formatMessage('debug', data));
                Logger.renderTimes.set(this, now);
            }
        }
    }

    private static renderTimes = new WeakMap<Logger, number>();
}

export function createLogger(options: LoggerOptions): Logger {
    return new Logger(options);
} 